import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComboLocation } from './ComboLocation';
import {
  Container,
  InputTitle,
  ErrorMessage,
  AddressContainer,
  Address,
  PlacesSuggestions,
  LocationCombosContainer,
  InputCEP,
  SearchButton,
  CepContainer,
  InputComplement,
  AddressFieldsContainerBR,
  ComplementContainer,
  InputWrapper,
  StyledInput
} from './styles';
import { isValidFormatCep, tabsConfig } from './utils';
import Tabs from '../../commons/Tabs';

const LocationCombosBR = ({
  cepDefault,
  onKeyDown,
  onCepChange,
  watch,
  cepError,
  cepNotFound,
  errors,
  breakMobile,
  onCepSearch,
  getValues,
  setAddressComplement,
  setGetSuggestions,
  setAddressValidation,
  setAddressValue,
  setValue,
  handleSelect,
  handleOnError,
  predictions,
  placesLoading,
  placesReady,
  onPlacesSearch,
  addressValue,
  addressValidation,
  handleSelectSuggestions,
  register,
  getSuggestions,
  hasCoordinates,
  address,
  isMobile,
  provinceData,
  cityData,
  districtData,
  subDistrictData,
  selectedProvince,
  selectedCity,
  selectedDistrict,
  selectedSubDistrict,
  siteCodes,
  handleLocationChange,
  cep,
  addressComplement,
  locationStepOperations,
  dispatch,
  isOnCEPView,
  setIsOnCEPV,
  hasSearchWithCep
}) => {
  const { t } = useTranslation();
  const cepValue = watch('cep');
  const readyToShowCombos = isOnCEPView && selectedProvince && !cepError && !errors?.cep;
  const showCombosOnAddressView = !isOnCEPView && (address || cep);
  const cepViewTabId = 1;
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  const [isCepAddressFocused, setIsCepAddressFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [cepActiveIndex, setCepActiveIndex] = useState(null);
  const skipBlurRef = useRef(false);
  const skipCepBlurRef = useRef(false);

  return (
    <Container data-publi='locationStep-locationSection-container'>
      <Tabs tabs={tabsConfig} onTabClick={id => setIsOnCEPV(id === cepViewTabId)} />
      {isOnCEPView ? (
        <CepContainer data-publi='locationStep-locationSection-cepContainer'>
          <InputTitle data-publi='locationStep-locationSection-inputTitle'>
            {t('professionalPubli.location.cep')}*
          </InputTitle>
          <InputCEP
            isMobile={isMobile}
            data-publi='locationStep-locationSection-inputCep'
            type='text'
            defaultValue={cepDefault}
            maxLength='10'
            onKeyDown={onKeyDown}
            placeholder={t('professionalPubli.location.cep.placeholder')}
            onChange={e => {
              dispatch(locationStepOperations.resetCepError());
              onCepChange(e);
            }}
            onBlur={onCepSearch}
            value={cepValue}
            hasError={cepError || (errors && errors.cep)}
          />
          {errors?.cep && (
            <ErrorMessage isOnCEPView={isOnCEPView} data-publi='locationStep-locationSection-cepContainer-error'>
              {t('professionalPubli.location.cep.error')}
            </ErrorMessage>
          )}
          <SearchButton
            isMobile={isMobile}
            data-publi='locationStep-locationSection-cepContainer-button'
            type='button'
            onClick={onCepSearch}
          >
            {t('professionalPubli.location.cep.CTA')}
          </SearchButton>
          {cepError && !errors?.cep && (
            <ErrorMessage isOnCEPView={isOnCEPView} data-publi='locationStep-locationSection-cepContainer-error'>
              {t(`professionalPubli.location.cep.${cepNotFound ? 'error.notFound' : 'invalid'}`)}
            </ErrorMessage>
          )}
        </CepContainer>
      ) : (
        <AddressFieldsContainerBR isOnCEPView={isOnCEPView}>
          <AddressContainer>
            <InputTitle data-publi='locationStep-locationSection-address-title' margin='0px 0px 5px'>
              {t('professionalPubli.location.address')}
            </InputTitle>
            <div data-publi='locationStep-locationSection-address-container'>
              <Address
                placeholder={t('professionalPubli.location.address.placeholder')}
                autoFocus={false}
                name='addressVal'
                ref={register}
                value={getValues('addressVal') ? getValues('addressVal') : addressValue}
                autoComplete='off'
                onFocus={() => setIsAddressFocused(true)}
                onBlur={() => {
                  if (skipBlurRef.current) return;
                  setIsAddressFocused(false);
                  handleSelectSuggestions(getValues('addressVal'));
                }}
                onChange={e => {
                  const value = e.target.value;
                  setAddressValidation(null);
                  setGetSuggestions(true);
                  setAddressValue(value);
                  setValue('addressVal', value);
                  onPlacesSearch(value).then(({ status }) => {
                    if (status === 'ZERO_RESULTS') {
                      handleOnError(status, () => setGetSuggestions(false));
                    }
                  });
                }}
                data-publi='locationStep-locationSection-address-location'
                spellcheck='false'
                hasError={errors.addressVal}
              />
              {!errors.addressVal && addressValidation}
              <ErrorMessage
                data-publi='locationStep-locationSection-address-validationError'
                errors={errors}
                name='addressVal'
                as='div'
              >
                {errors.addressVal && <p>{t('professionalPubli.location.address.error')}</p>}
              </ErrorMessage>
              {placesReady && isAddressFocused && getSuggestions && predictions.length > 0 && (
                <PlacesSuggestions data-publi='location-address-suggestion'>
                  {placesLoading && (
                    <div data-publi='locationStep-locationSection-suggestion-loading' className='suggestion-item'>
                      loading....
                    </div>
                  )}
                  {predictions.map((prediction, index) => {
                    const className = activeIndex === index ? 'suggestion-item active' : 'suggestion-item';
                    return (
                      <div
                        data-publi={`location-suggestion-${index}`}
                        className={className}
                        key={`${prediction.place_id}-${index}`}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                        onMouseDown={() => {
                          skipBlurRef.current = true;
                          handleSelect(prediction.description, prediction.place_id, prediction, true);
                          setIsAddressFocused(false);
                          setTimeout(() => {
                            skipBlurRef.current = false;
                          }, 0);
                        }}
                        onTouchStart={() => {
                          skipBlurRef.current = true;
                          handleSelect(prediction.description, prediction.place_id, prediction, true);
                          setIsAddressFocused(false);
                          setTimeout(() => {
                            skipBlurRef.current = false;
                          }, 0);
                        }}
                      >
                        {prediction.description}
                      </div>
                    );
                  })}
                </PlacesSuggestions>
              )}
            </div>
          </AddressContainer>
        </AddressFieldsContainerBR>
      )}
      {(readyToShowCombos || showCombosOnAddressView) && (
        <LocationCombosContainer isMobile={isMobile}>
          <ComboLocation
            data-publi='locationStep-locationSection-combo-province'
            isMobile={isMobile}
            title={t('professionalPubli.location.province')}
            onChange={e => handleLocationChange(e, 'province')}
            value={selectedProvince && selectedProvince.location_id}
            disabled={!address && !cep}
            id='provinceSelect'
            placeholder={t('professionalPubli.location.province.placeholder')}
            filterPlaceholder={t('professionalPubli.location.province.filterPlaceholder')}
            locationData={provinceData}
            errors={errors.provinceVal}
            noOptionToClean
          />
          <ComboLocation
            data-publi='locationStep-locationSection-combo-city'
            isMobile={isMobile}
            title={t('professionalPubli.location.city')}
            onChange={e => handleLocationChange(e, 'city')}
            value={selectedCity && selectedCity.location_id}
            disabled={!selectedProvince || !selectedProvince.location_id}
            id='citySelect'
            placeholder={t('professionalPubli.location.city.placeholder')}
            filterPlaceholder={t('professionalPubli.location.city.filterPlaceholder')}
            locationData={cityData}
            errors={!errors.provinceVal && errors.cityVal}
          />
          {(readyToShowCombos || showCombosOnAddressView) && districtData && districtData?.length !== 0 && (
            <ComboLocation
              data-publi='locationStep-locationSection-combo-district'
              isMobile={isMobile}
              title={t('professionalPubli.location.zone')}
              onChange={e => handleLocationChange(e, 'district')}
              value={selectedDistrict && selectedDistrict.location_id}
              disabled={!selectedCity || districtData?.length === 0}
              id='districtSelect'
              placeholder={t('professionalPubli.location.zone.placeholder')}
              filterPlaceholder={t('professionalPubli.location.zone.filterPlaceholder')}
              locationData={districtData}
              errors={errors.districtVal}
            />
          )}
          {subDistrictData?.length !== 0 && (
            <ComboLocation
              data-publi='locationStep-locationSection-combo-subdistrict'
              isMobile={isMobile}
              title={t('professionalPubli.location.subzone')}
              onChange={e => handleLocationChange(e, 'subdistrict')}
              value={selectedSubDistrict && selectedSubDistrict.location_id}
              disabled={!subDistrictData || subDistrictData?.length === 0}
              id='subDistrictSelect'
              placeholder={t(' ')}
              filterPlaceholder={t(' ')}
              locationData={subDistrictData}
              errors={errors.subDistrictVal}
            />
          )}
          {!isOnCEPView ? (
            <InputWrapper isMobile={isMobile}>
              <InputTitle data-publi='locationStep-locationSection-complement-postalcode'>
                {t('professionalPubli.location.cep')}
              </InputTitle>
              <StyledInput
                data-publi='locationStep-locationSection-complement-postalcode-input'
                // name={'postalCode'}
                // ref={register}
                value={cepValue}
                onChange={e => {
                  const value = e.target.value;
                  if (isValidFormatCep(value)) {
                    setValue('cep', value);
                  }
                }}
                placeholder={t('professionalPubli.location.cep.placeholder')}
              />
              <ErrorMessage
                data-publi='locationStep-locationSection-postalCode-validationError'
                errors={errors}
                name='postalCode'
                as='div'
              >
                {errors.postalCode && <p>{t('professionalPubli.location.cep.error')}</p>}
              </ErrorMessage>
            </InputWrapper>
          ) : (
            <AddressFieldsContainerBR isMobile={isMobile} isOnCEPView={isOnCEPView}>
              <AddressContainer>
                <InputTitle data-publi='locationStep-locationSection-address-title' margin='0px 0px 5px'>
                  {t('professionalPubli.location.address')}
                </InputTitle>
                <div data-publi='locationStep-locationSection-address-container'>
                  <Address
                    placeholder={t('professionalPubli.location.address.placeholder')}
                    autoFocus={false}
                    name='addressVal'
                    ref={register}
                    value={getValues('addressVal') ? getValues('addressVal') : addressValue}
                    autoComplete='off'
                    onFocus={() => setIsCepAddressFocused(true)}
                    onBlur={() => {
                      if (skipCepBlurRef.current) return;
                      setIsCepAddressFocused(false);
                      handleSelectSuggestions(getValues('addressVal'));
                    }}
                    onChange={e => {
                      const value = e.target.value;
                      setAddressValidation(null);
                      setGetSuggestions(true);
                      setAddressValue(value);
                      setValue('addressVal', value);
                      onPlacesSearch(value).then(({ status }) => {
                        if (status === 'ZERO_RESULTS') {
                          handleOnError(status, () => setGetSuggestions(false));
                        }
                      });
                    }}
                    data-publi='locationStep-locationSection-address-location'
                    spellcheck='false'
                    hasError={errors.addressVal}
                    isOnCEPView={isOnCEPView}
                  />
                  <ErrorMessage
                    isOnCEPView={isOnCEPView}
                    data-publi='locationStep-locationSection-address-validationError'
                    errors={errors}
                    name='addressVal'
                    as='div'
                  >
                    {errors.addressVal && <p>{t('professionalPubli.location.address.error')}</p>}
                  </ErrorMessage>
                  {placesReady && isCepAddressFocused && getSuggestions && predictions.length > 0 && (
                    <PlacesSuggestions data-publi='location-address-suggestion'>
                      {placesLoading && (
                        <div data-publi='locationStep-locationSection-suggestion-loading' className='suggestion-item'>
                          loading....
                        </div>
                      )}
                      {predictions.map((prediction, index) => {
                        const className = cepActiveIndex === index ? 'suggestion-item active' : 'suggestion-item';
                        return (
                          <div
                            data-publi={`location-suggestion-${index}`}
                            className={className}
                            key={`${prediction.place_id}-${index}`}
                            onMouseEnter={() => setCepActiveIndex(index)}
                            onMouseLeave={() => setCepActiveIndex(null)}
                            onMouseDown={() => {
                              skipCepBlurRef.current = true;
                              handleSelect(prediction.description, prediction.place_id, prediction, false);
                              setIsCepAddressFocused(false);
                              setTimeout(() => {
                                skipCepBlurRef.current = false;
                              }, 0);
                            }}
                            onTouchStart={() => {
                              skipCepBlurRef.current = true;
                              handleSelect(prediction.description, prediction.place_id, prediction, false);
                              setIsCepAddressFocused(false);
                              setTimeout(() => {
                                skipCepBlurRef.current = false;
                              }, 0);
                            }}
                          >
                            {prediction.description}
                          </div>
                        );
                      })}
                    </PlacesSuggestions>
                  )}
                </div>
              </AddressContainer>
            </AddressFieldsContainerBR>
          )}
          <ComplementContainer isMobile={isMobile}>
            <InputTitle data-publi='locationStep-locationSection-complement-title'>
              {t('publisher.step1.module2.complement.title')}
            </InputTitle>
            <InputComplement
              type='text'
              name='addressComplement'
              ref={register}
              value={addressComplement}
              placeholder={t('publisher.step1.module2.complement.placeholder')}
              onChange={e => {
                setAddressComplement(e.target.value);
              }}
              autoComplete='off'
            />
            <ErrorMessage
              data-publi='locationStep-locationSection-complement-validationError'
              errors={errors}
              name='addressComplement'
              as='div'
            >
              {errors.addressComplement && <p>{t(errors.addressComplement.message)}</p>}
            </ErrorMessage>
          </ComplementContainer>
        </LocationCombosContainer>
      )}
      <input
        type='hidden'
        data-publi='locationStep-location-address-coord'
        name='coordinates'
        ref={register}
        value={[hasCoordinates.lat, hasCoordinates.lng]}
      />
    </Container>
  );
};

export default LocationCombosBR;
